/**
 * AdminPortalLayout — slim banded chrome for the portal-styled admin
 * SECTIONS (e.g. palautteet at `/admin/feedback`); `chrome: 'portal'` in
 * the registry (025-admin-area, FR-009/FR-011, research RD-005). The
 * launch-portal home at `/admin` does NOT use this chrome — it uses the
 * bandless `AdminPortalHomeLayout` (`chrome: 'portal-home'`).
 *
 * This is the sibling of `AdminCmsLayout`: the same minimal "logo +
 * hairline divider + page-title `<h1>`" top band, the same SkipLink-first
 * ordering, the same focusable `<main>` landmark. The CMS keeps its own
 * `AdminCmsLayout`; this chrome serves the portal-styled sections (e.g.
 * palautteet) that are NOT the CMS. The model is
 * portal-home-plus-per-section-chrome — no single shell wraps all four
 * sections (RD-005); each section owns its chrome and a back-to-portal
 * affordance, while the portal home owns its own bandless shell.
 *
 * Structure (mirrors `AdminCmsLayout` exactly):
 *
 *   - `<SkipLink />` first, so keyboard users get the canonical "Siirry
 *     pääsisältöön" affordance the moment they Tab into the page (FR-005).
 *   - `<header>` (banner landmark) with the YTK logo wrapped in a
 *     `<Link to="/">` (the brand mark routes to the member home, parity
 *     with the member TopBar and `AdminCmsLayout`), an `aria-hidden`
 *     vertical hairline divider, and the page-title `<h1>` reading
 *     `t('admin.portal.title')` (Finnish-first "Ylläpito"). One
 *     page-level `<h1>` for the whole shell; routed content starts its
 *     own headings at `<h2>`.
 *   - `<main id="main-content" tabIndex={-1}>` carrying the route
 *     children. The id matches the SkipLink hash target and the
 *     `tabIndex={-1}` makes the landmark programmatically focusable so the
 *     hash-jump receives keyboard focus.
 *
 * ## Back-to-portal affordance slot
 *
 * The header carries an OPTIONAL `backToPortal` render slot positioned at
 * the right edge of the band (`ml-auto` pushes it past the title). The
 * slot is the clean extension seam RD-005 calls for: the portal-styled
 * sections that mount this chrome (palautteet) pass their own
 * back-to-`/admin` affordance element into the slot. A section that passes
 * nothing renders NO back-to-portal control: the slot wrapper only mounts
 * when `backToPortal` is provided, so a section without one emits no empty
 * right-hand node — the "no affordance unless supplied" contract is
 * structural, not merely visual. (The portal home, which has nowhere "back"
 * to go, lives in `AdminPortalHomeLayout` and never reaches this slot.)
 * Keeping the affordance a caller-supplied
 * `ReactNode` (rather than a baked-in `<Link>`) lets each section decide
 * the exact label, target, and icon without this chrome growing
 * section-specific knowledge.
 *
 * ## Token-only styling
 *
 * Every utility below is layout (`flex`, `gap-*`, `px-*`, `ml-auto`) or a
 * token-backed colour/spacing utility (`bg-background`, `bg-top-bar`,
 * `border-border`, `h-cms-top-bar`, `text-foreground`). The class strings
 * are shared, verbatim, with `AdminCmsLayout` so the two admin shells read
 * identically; the `h-cms-top-bar` token (3.25rem / 52px, `tokens.css`) is
 * reused rather than forked — the portal band is the same height as the
 * CMS band by design. No raw hex, no `*-[…]` bracket utilities
 * (FR-047 / no-token-bypass).
 */

import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router';

import ytkLogo from '../assets/ytk-logo.png';
import { useTranslate } from '../i18n/useTranslate.js';

import { SkipLink } from './SkipLink.js';

const shellClasses = 'flex min-h-screen flex-col bg-background';
// Slim sticky top band — shared verbatim with `AdminCmsLayout`. `h-cms-top-bar`
// is the 3.25rem (52px) token in `tokens.css`; the surface paints `bg-top-bar`
// (the pure-white token the member TopBar uses) with a hairline `border-b`.
const headerClasses =
  'sticky top-0 z-30 flex h-cms-top-bar items-center gap-3 ' +
  'border-b border-border bg-top-bar px-4';
// Logo link focus-visible treatment — matches the rest of the chrome surfaces
// (ring-ring, ring-offset-2) so keyboard focus reads consistently.
const brandLinkClasses =
  'inline-flex items-center rounded-md no-underline transition active:scale-95 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const brandLogoClasses = 'h-7 w-auto';
// Vertical hairline divider between the logo and the title — decorative, so
// `aria-hidden`. Drawn with `w-px h-5` against the border token.
const dividerClasses = 'h-5 w-px bg-border';
// Page title — semantic `<h1>` for the route. Same weight as the CMS shell's
// title so the two admin chromes read identically.
const titleClasses = 'm-0 text-sm font-semibold tracking-tight text-foreground';
// Back-to-portal slot wrapper — `ml-auto` pushes it to the right edge of the
// band, past the title. Only mounted when the caller supplies `backToPortal`.
const backToPortalSlotClasses = 'ml-auto flex items-center';
// Main fills the remaining vertical space below the sticky header (mirrors
// `AdminCmsLayout`: `flex-1` + `flex flex-col min-h-0` so routed content can
// stretch and contain its own `overflow-y-auto` regions).
const mainClasses = 'flex flex-1 flex-col min-h-0';

export interface AdminPortalLayoutProps {
  /**
   * Routed content rendered inside the `<main>` landmark.
   */
  readonly children?: ReactNode;
  /**
   * Optional back-to-portal affordance rendered at the right edge of the
   * header band. Omitted on the portal home (`/admin`) — the portal has no
   * "back" target — and supplied by the sections that mount this chrome
   * (palautteet) as their own back-to-`/admin` element. When omitted, no
   * right-hand slot node is rendered at all.
   */
  readonly backToPortal?: ReactNode;
}

export function AdminPortalLayout({ children, backToPortal }: AdminPortalLayoutProps): JSX.Element {
  const t = useTranslate();

  return (
    <div className={shellClasses}>
      <SkipLink />
      <header className={headerClasses}>
        <Link to="/" className={brandLinkClasses}>
          <img src={ytkLogo} alt={t('common.brandFullName')} className={brandLogoClasses} />
        </Link>
        <span aria-hidden="true" className={dividerClasses} />
        <h1 className={titleClasses}>{t('admin.portal.title')}</h1>
        {backToPortal !== undefined ? (
          <div className={backToPortalSlotClasses}>{backToPortal}</div>
        ) : null}
      </header>
      <main id="main-content" tabIndex={-1} className={mainClasses}>
        {children}
      </main>
    </div>
  );
}
